import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { FeatureId, PermissionMap } from '../types/permissions';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  phone: string;
  company_name?: string;
  vat_number?: string;
  billing_address?: string;
  crib_number?: string;
  kvk_number?: string;
  is_admin: boolean;
  role: string;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  isAdmin: boolean;
  isWorker: boolean;
  permissions: PermissionMap;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string, phone: string, role?: string, companyName?: string) => Promise<void>;
  signOut: () => Promise<void>;
  checkPermission: (featureId: FeatureId) => boolean;
  updateProfile: (data: Partial<UserProfile>) => Promise<void>;
  isProfileComplete: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isWorker, setIsWorker] = useState(false);
  const [permissions, setPermissions] = useState<PermissionMap>({});

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      // Handle refresh token errors by clearing invalid tokens
      if (error && (
        error.message?.includes('Invalid Refresh Token') ||
        error.message?.includes('refresh_token_not_found') ||
        error.message?.includes('Refresh Token Not Found')
      )) {
        // Clear the invalid token from storage
        localStorage.removeItem(`sb-${supabase.supabaseUrl.split('//')[1].split('.')[0]}-auth-token`);
        
        // Reset all auth state
        setUser(null);
        setUserProfile(null);
        setIsAdmin(false);
        setIsWorker(false);
        setPermissions({});
        
        console.warn('Invalid refresh token cleared. User will need to log in again.');
        return;
      }
      
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserProfile(session.user.id);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Handle token refresh errors
      if (event === 'TOKEN_REFRESHED' && !session) {
        // Clear invalid token and reset state
        localStorage.removeItem(`sb-${supabase.supabaseUrl.split('//')[1].split('.')[0]}-auth-token`);
        setUser(null);
        setUserProfile(null);
        setIsAdmin(false);
        setIsWorker(false);
        setPermissions({});
        console.warn('Token refresh failed. User will need to log in again.');
        return;
      }
      
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserProfile(session.user.id);
      } else {
        setIsAdmin(false);
        setIsWorker(false);
        setPermissions({});
        setUserProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user profile:', error);
        return;
      }

      if (data) {
        setUserProfile(data as UserProfile);
        setIsAdmin(data.is_admin || data.role === 'admin');
        setIsWorker(data.role === 'worker');
        
        // Fetch permissions if worker
        if (data.role === 'worker') {
          fetchPermissions(data.role);
        } else if (data.is_admin || data.role === 'admin') {
          // Admins have all permissions
          const allPermissions: PermissionMap = {
            order_management: true,
            user_management: true,
            worker_management: true,
            event_management: true,
            category_management: true,
            event_registrations: true,
            track_management: true,
            reports_analytics: true,
            calendar_view: true,
            email_templates: true,
            system_settings: true,
            customer_management: true
          };
          setPermissions(allPermissions);
        } else {
          // Regular users have no admin permissions
          setPermissions({});
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchPermissions = async (role: string) => {
    try {
      const { data, error } = await supabase
        .from('role_permissions')
        .select('*')
        .eq('role', role);

      if (error) throw error;

      // Convert to permission map
      const permMap: PermissionMap = {};
      (data || []).forEach((perm) => {
        permMap[perm.feature_id] = perm.can_access;
      });

      setPermissions(permMap);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setPermissions({});
    }
  };

  const signUp = async (
    email: string, 
    password: string, 
    fullName: string, 
    phone: string, 
    role: string = 'user',
    companyName?: string
  ) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            phone,
            role,
            company_name: companyName
          },
        },
      });

      if (error) throw error;

      if (data.user) {
        const profileData: any = {
          id: data.user.id,
          email: email,
          full_name: fullName,
          phone: phone,
          role: role
        };
        
        if (role === 'promotor' && companyName) {
          profileData.company_name = companyName;
        }
        
        const { error: profileError } = await supabase
          .from('users')
          .insert([profileData]);

        if (profileError) {
          await supabase.auth.signOut();
          throw new Error('Failed to create user profile. Please try again.');
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message || 'Failed to register. Please try again.');
      }
      throw new Error('Failed to connect to the server. Please check your internet connection.');
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;

      // Check if user exists in users table
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', data.user.id)
        .maybeSingle();

      if (!userData) {
        // User doesn't exist in users table, create profile
        const { error: createError } = await supabase
          .from('users')
          .insert([
            {
              id: data.user.id,
              email: data.user.email,
              full_name: data.user.user_metadata.full_name || '',
              phone: data.user.user_metadata.phone || '',
              role: data.user.user_metadata.role || 'user',
              company_name: data.user.user_metadata.company_name || null
            },
          ]);

        if (createError) {
          throw new Error('Failed to create user profile');
        }
      } else if (userError) {
        throw userError;
      }

      // Check if user has temporary password
      if (userData?.temporary_password) {
        // Store temporary password flag in session storage for redirect
        sessionStorage.setItem('hasTemporaryPassword', 'true');
        sessionStorage.setItem('userId', data.user.id);
      }

      // Fetch user profile
      await fetchUserProfile(data.user.id);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message || 'Failed to login. Please check your credentials.');
      }
      throw new Error('Failed to connect to the server. Please check your internet connection.');
    }
  };

  const signOut = async () => {
    try {
      // First clear the local state
      setUser(null);
      setUserProfile(null);
      setIsAdmin(false);
      setIsWorker(false);
      setPermissions({});

      // Then attempt to sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        // Check if the error is due to missing session
        if (error.message?.includes('session_not_found') || 
            error.message?.includes('JWT expired') ||
            error.message?.includes('Auth session missing')) {
          // Session is already invalid/expired, just log it
          console.warn('Session was already expired or invalid');
          return;
        }
        // For other types of errors, throw them
        throw error;
      }
    } catch (error) {
      // Only throw error for non-session related issues
      if (error instanceof Error && 
          !error.message?.includes('session_not_found') && 
          !error.message?.includes('JWT expired') &&
          !error.message?.includes('Auth session missing')) {
        throw new Error('Failed to sign out properly. Please try again.');
      }
    }
  };

  const checkPermission = (featureId: FeatureId): boolean => {
    if (isAdmin) return true;
    return !!permissions[featureId];
  };

  const updateProfile = async (data: Partial<UserProfile>) => {
    if (!user) throw new Error('User not authenticated');
    
    try {
      const { error } = await supabase
        .from('users')
        .update(data)
        .eq('id', user.id);
        
      if (error) throw error;
      
      // Refresh user profile
      await fetchUserProfile(user.id);
      
      return;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw new Error('Failed to update profile');
    }
  };

  const isProfileComplete = (): boolean => {
    if (!userProfile) return false;
    
    // For promotors, check if they have company name, CRIB and KVK numbers
    if (userProfile.role === 'promotor') {
      return !!(
        userProfile.company_name && 
        userProfile.crib_number && 
        userProfile.kvk_number
      );
    }
    
    // For other users, basic profile is enough
    return true;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      userProfile,
      isAdmin, 
      isWorker, 
      permissions,
      signIn, 
      signUp, 
      signOut,
      checkPermission,
      updateProfile,
      isProfileComplete
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}